import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fileTag } from '../../utils/reviewApi';
import useDropdownFit from '../../utils/useDropdownFit';

/* Small shared pieces for the Doc Review screens. All classes are `drv-` (Note #15). */

export function initials(name, fallback = '?') {
  const parts = String(name || '').replace(/[._]/g, ' ').split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || fallback).slice(0, 2).toUpperCase();
}

export function Avatar({ name, color, ai }) {
  return (
    <span className="drv-av" style={ai ? { background: '#3D2070' } : color ? { background: color } : undefined}
      aria-hidden="true">
      {ai ? 'AI' : initials(name)}
    </span>
  );
}

export function FileChip({ fileName, kind }) {
  const k = kind || 'other';
  return <span className={`drv-ft drv-ft--${k}`}>{fileTag(fileName)}</span>;
}

export function RoutingPill({ state }) {
  const s = String(state || '').trim();
  if (!s) return <span className="drv-muted">—</span>;
  const low = s.toLowerCase();
  const cls = low === 'draft' ? 'draft' : low === 'approved' ? 'done'
    : low.includes('rout') ? 'routing' : 'none';
  return <span className={`drv-pill drv-pill--${cls}`}>{s}</span>;
}

export function ProgressBar({ done, total, color, wide, label }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className={`drv-bar${wide ? ' drv-bar--wide' : ''}`} role="img"
      aria-label={label || `${done} of ${total}`}>
      <i style={{ width: `${pct}%`, ...(color ? { background: color } : {}) }} />
    </div>
  );
}

/** "Due in 2 days" / "Due Oct 15" / "2 days late" — and how loudly to say it. */
export function dueText(dueDate, daysLeft) {
  if (!dueDate) return { text: 'No due date', tone: 'none' };
  if (daysLeft == null) return { text: `Due ${dueDate}`, tone: 'none' };
  if (daysLeft < 0) return { text: `${-daysLeft} day${daysLeft === -1 ? '' : 's'} late`, tone: 'late' };
  if (daysLeft === 0) return { text: 'Due today', tone: 'late' };
  if (daysLeft <= 2) return { text: `Due in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`, tone: 'late' };
  return { text: `Due ${fmtDate(dueDate)}`, tone: 'none' };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-10-12` or a UTC stamp → `Oct 12`. Dates only — no clock, so no timezone question. */
export function fmtDate(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  if (!m) return value || '';
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
}

/** Relative time for a UTC stamp (`2026-10-06 09:02:11`), as the bell shows it. */
export function ago(stamp) {
  if (!stamp) return '';
  const t = Date.parse(String(stamp).replace(' ', 'T') + (/[zZ]|[+-]\d\d:?\d\d$/.test(stamp) ? '' : 'Z'));
  if (Number.isNaN(t)) return fmtDate(stamp);
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return fmtDate(stamp);
}

/**
 * Pick reviewers from the account's people. `value` is [{user_id, display_name}].
 * Keyboard: ↑/↓ to move, Enter to add, Backspace on an empty box removes the last chip.
 */
export function PeoplePicker({ people, value, onChange, placeholder = 'Add a person…', autoFocus }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const fieldRef = useRef(null);
  const listStyle = useDropdownFit(fieldRef, open);

  const chosen = useMemo(() => new Set(value.map((v) => v.user_id)), [value]);
  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    return (people || []).filter((p) => !chosen.has(p.user_id)
      && (!q || `${p.display_name} ${p.email} ${p.user_id}`.toLowerCase().includes(q))).slice(0, 30);
  }, [people, chosen, text]);

  useEffect(() => { setActive(0); }, [text]);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const add = (p) => {
    onChange([...value, { user_id: p.user_id, display_name: p.display_name }]);
    setText('');
    inputRef.current?.focus();
  };
  const remove = (uid) => onChange(value.filter((v) => v.user_id !== uid));

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && matches[active]) add(matches[active]); }
    else if (e.key === 'Escape') setOpen(false);
    else if (e.key === 'Backspace' && !text && value.length) remove(value[value.length - 1].user_id);
  };

  return (
    <div className="drv-picker" ref={boxRef}>
      <div ref={fieldRef} className={`drv-picker-box${focus ? ' drv-picker-box--focus' : ''}`}
        onClick={() => inputRef.current?.focus()}>
        {value.map((v) => (
          <span className="drv-chip" key={v.user_id}>
            <Avatar name={v.display_name} />
            {v.display_name}
            <button type="button" aria-label={`Remove ${v.display_name}`}
              onClick={(e) => { e.stopPropagation(); remove(v.user_id); }}>×</button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={text}
          autoFocus={autoFocus}
          aria-label="Add a reviewer"
          placeholder={value.length ? '' : placeholder}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => { setFocus(true); setOpen(true); }}
          onBlur={() => setFocus(false)}
          onKeyDown={onKey}
        />
      </div>
      {open && (
        <div className="drv-options" role="listbox" aria-label="People" style={listStyle}>
          {matches.length === 0 ? (
            <div className="drv-option drv-option-sub">
              {people?.length ? 'No one else matches.' : 'Loading people…'}
            </div>
          ) : matches.map((p, i) => (
            <button type="button" role="option" aria-selected={i === active} key={p.user_id}
              className={`drv-option${i === active ? ' drv-option--active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => add(p)}>
              <Avatar name={p.display_name} color={p.avatar_color} />
              <span>{p.display_name}{p.is_me ? ' (you)' : ''}</span>
              <span className="drv-option-sub">{p.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Escape closes, the backdrop closes — the modal shell every Doc Review dialog uses. */
export function Modal({ title, subtitle, onClose, children, footer, labelledBy = 'drv-modal-title' }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="drv-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="drv-modal" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
        <div className="drv-modal-head">
          <div>
            <h2 id={labelledBy}>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button type="button" className="drv-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="drv-modal-body">{children}</div>
        {footer && <div className="drv-modal-foot">{footer}</div>}
      </section>
    </div>
  );
}

/**
 * A searchable single-select — type to narrow, ↑/↓ + Enter to pick, Escape to close.
 * `options` is [{value, label}]. Used for the Orcanos filter list, which runs to hundreds.
 */
export function SearchSelect({ options, value, onChange, placeholder = 'Search…', disabled, emptyText = 'No matches', ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const fieldRef = useRef(null);
  // The list is PORTALLED to <body> with fixed coordinates: inside a table or a scrolling card an
  // absolute list was clipped by the box and could not be read or reached.
  const listStyle = useDropdownFit(fieldRef, open && !disabled, { fixed: true });
  const listId = useMemo(() => `drv-list-${Math.random().toString(36).slice(2, 9)}`, []);

  const current = (options || []).find((o) => String(o.value) === String(value));
  const matches = useMemo(() => {
    const words = text.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (options || []).filter((o) => {
      const hay = String(o.label).toLowerCase();
      return words.every((w) => hay.includes(w));
    }).slice(0, 200);
  }, [options, text]);

  useEffect(() => { setActive(0); }, [text, open]);
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      setOpen(false); setText('');
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (o) => {
    onChange(o.value);
    setText('');
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && matches[active]) pick(matches[active]); else setOpen(true); }
    else if (e.key === 'Escape') { setOpen(false); setText(''); }
  };

  return (
    <div className="drv-picker" ref={boxRef}>
      <div className="drv-combo" ref={fieldRef}>
        <input
          ref={inputRef}
          className="drv-input drv-combo-input"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          disabled={disabled}
          value={open ? text : (current?.label || '')}
          placeholder={open && current ? current.label : placeholder}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKey}
          autoComplete="off"
        />
        <span className="drv-combo-arrow" aria-hidden="true">▾</span>
      </div>
      {open && !disabled && createPortal(
        <div className="drv-options drv-options--float" role="listbox" id={listId} ref={listRef} aria-label={ariaLabel}
          style={listStyle}>
          {matches.length === 0 ? (
            <div className="drv-option drv-option-sub">{emptyText}</div>
          ) : matches.map((o, i) => (
            <button type="button" role="option" key={o.value} data-active={i === active}
              aria-selected={String(o.value) === String(value)}
              className={`drv-option${i === active ? ' drv-option--active' : ''}${String(o.value) === String(value) ? ' drv-option--chosen' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(o)}>
              {o.strong ? <strong>{o.label}</strong> : o.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
